import { buildAIPrompt, SYMPTOM_NAMES } from './aiPromptTemplate';
/**
 * Build context summary from weight loss survey answers
 */
export function buildWeightContext(answers) {
  const contextParts = [];

  // Safety check
  if (answers.safety) {
    if (answers.safety.includes('none')) {
      contextParts.push('- Good health condition, no specific risks');
    } else {
      contextParts.push('- Some health concerns to note');
    }
  }

  // Demographics
  if (answers.demographics) {
    const { age, height, weight } = answers.demographics;
    if (age) contextParts.push(`- Age group: ${age}`);
    if (height) contextParts.push(`- Height: ${height}`);
    if (weight) contextParts.push(`- Weight: ${weight}`);
  }

  // Goal
  if (answers.goal) {
    const goalMap = {
      '5-10': 'Goal: Lose 2.5-5kg',
      '10-20': 'Goal: Lose 5-10kg',
      '>20': 'Goal: Lose over 10kg'
    };
    contextParts.push(`- ${goalMap[answers.goal]}`);
  }

  // Main issues
  if (answers.mainIssue) {
    const issues = answers.mainIssue.map(issue => {
      switch (issue) {
        case 'edema': return 'Prone to edema';
        case 'appetite': return 'Strong appetite';
        case 'constipation': return 'Frequent constipation';
        case 'fatigue': return 'Easily fatigued';
        default: return issue;
      }
    });
    contextParts.push(`- Main concerns: ${issues.join(', ')}`);
  }

  // Edema details
  if (answers.edemaTime) {
    const edemaMap = {
      'morning': 'Edema obvious in the morning',
      'evening': 'Edema obvious in afternoon/evening',
      'allday': 'Edema all day'
    };
    contextParts.push(`- ${edemaMap[answers.edemaTime]}`);
  }

  // Appetite details
  if (answers.appetiteType) {
    const appetiteMap = {
      'meals': 'Eat a lot at main meals',
      'snacks': 'Always craving snacks',
      'sweet_oily': 'Love sweets and greasy food'
    };
    contextParts.push(`- ${appetiteMap[answers.appetiteType]}`);
  }

  // Diet habits
  if (answers.dietHabits && answers.dietHabits.length > 0) {
    const habits = answers.dietHabits
      .filter(h => h !== 'healthy')
      .map(habit => {
        switch (habit) {
          case 'heavy_taste': return 'Prefer strong flavors';
          case 'cold_drinks': return 'Love cold drinks';
          case 'eat_out': return 'Eat out frequently';
          case 'fast_eating': return 'Eat fast';
          default: return habit;
        }
      });
    if (habits.length > 0) {
      contextParts.push(`- Diet habits: ${habits.join(', ')}`);
    }
  }

  // Exercise
  if (answers.exercise) {
    const exerciseMap = {
      'none': 'Rarely exercise',
      'occasional': 'Occasional exercise',
      'regular': 'Regular exercise'
    };
    contextParts.push(`- Exercise: ${exerciseMap[answers.exercise]}`);
  }

  // Previous attempts
  if (answers.previousAttempts && answers.previousAttempts.length > 0) {
    const attempts = answers.previousAttempts
      .filter(a => a !== 'none')
      .map(attempt => {
        switch (attempt) {
          case 'diet': return 'Dieting';
          case 'exercise': return 'Exercise for weight loss';
          case 'pills': return 'Weight loss pills';
          case 'tcm': return 'TCM conditioning';
          default: return attempt;
        }
      });
    if (attempts.length > 0) {
      contextParts.push(`- Tried before: ${attempts.join(', ')}`);
    }
  }

  // Constitution
  if (answers.constitution && answers.constitution.length > 0) {
    const constitution = answers.constitution
      .filter(c => c !== 'none')
      .map(c => {
        switch (c) {
          case 'cold_limbs': return 'Cold hands and feet';
          case 'sweating': return 'Easily sweating';
          case 'thirsty': return 'Dry mouth/thirsty';
          case 'loose_stool': return 'Loose stools';
          default: return c;
        }
      });
    if (constitution.length > 0) {
      contextParts.push(`- Constitution: ${constitution.join(', ')}`);
    }
  }

  return contextParts.join('\n');
}

/**
 * Build AI prompt for weight loss questions
 */
export function buildWeightPrompt(question, answers, userName) {
  const context = buildWeightContext(answers);
  return buildAIPrompt(SYMPTOM_NAMES.weight, context, question.text, userName);
}